import { mutatePurchaseOrderCategories } from "@/api/purchaseOrderCategories/usePurchaseOrderCategoriesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { handleKeyDown } from "@/utils/event";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { ScmApiPurchaseOrderCategoriesPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
});

interface PurchaseOrderCategoryCreateFormProps {
  name?: string;
  autoCompleteOnSubmit?: (values: any) => void;
}

export const PurchaseOrderCategoryCreateForm = (params: PurchaseOrderCategoryCreateFormProps) => {
  const { name, autoCompleteOnSubmit } = params;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: createMutate } = useMutation(
    (params: ScmApiPurchaseOrderCategoriesPostRequest) =>
      mutatePurchaseOrderCategories
        .create(params)
        .mutationFn(params as ScmApiPurchaseOrderCategoriesPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["purchaseOrderCategories"]);
        closeModal(form.values);
        customAlert(
          `${t("message.createSuccess", { item: `${t("purchaseOrderCategory.category")}` })}`,
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("purchaseOrderCategory.name") }),
          `${t("common.createFail", { item: `${t("purchaseOrderCategory.category")}` })}`,
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      name: name ?? undefined,
    },
  });

  const onSubmit = async () => {
    try {
      createMutate({
        purchaseOrderCategoriesGetRequest: {
          name: name ?? form.values.name!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("purchaseOrderCategory.name") }),
        `${t("common.createFail", { item: `${t("purchaseOrderCategory.category")}` })}`,
        "red",
      );
    }
  };

  return (
    <Flex w="100%" direction="column" gap="md">
      <Create.Input
        w="20rem"
        label={t("purchaseOrderCategory.category")}
        placeholder={t("placeholder.input", { item: `${t("purchaseOrderCategory.name")}` })}
        withAsterisk
        {...form.getInputProps("name")}
        onKeyDown={(e) =>
          handleKeyDown(e, () =>
            autoCompleteOnSubmit ? autoCompleteOnSubmit(form.values) : onSubmit(),
          )
        }
      />
      <Create.ButtonBox>
        <Create.Button color="gray" onClick={closeModal}>
          {t("common.cancel")}
        </Create.Button>
        <Create.Button
          onClick={() => (autoCompleteOnSubmit ? autoCompleteOnSubmit(form.values) : onSubmit())}
          disabled={validateFormValues({
            pageType: "itemCategory",
            values: form.values,
          })}
        >
          {t("common.create", { item: `${t("purchaseOrderCategory.category")}` })}
        </Create.Button>
      </Create.ButtonBox>
    </Flex>
  );
};
